import { BuildClass, MaybeT, t, TW_OVERFLOW_ELLIPSIS } from '../../../universal';
import { _, React } from '../../lib';
import { uploadFileIris } from '../component-upload';
import { LoadingSpinnerLarge } from './loading';
import { CJSX, MatchRender, useRSInstance, useStateSync } from './meta-types';
import { stubInput } from './stubs';
export const FileUploadItemT = t.Object({
    Filename: t.String(),
    Checksum: MaybeT(t.String()),
    Data: MaybeT(t.String()),
});
const getDefaultState = (p) => ({
    files: p.value ?? [],
    isUploadingProgress: null,
    isDraggingOver: false,
    isFocused: false,
});
export const FileUpload = (props) => {
    const rs = useRSInstance({
        props,
        defaultState: getDefaultState,
        refs: {
            inputRef: React.useRef(stubInput),
        },
        actionToDelta: null,
    });
    useStateSync({
        propVal: props.value,
        stateVal: rs.state.files,
        setState: files => {
            rs.updateState({ files });
        },
        setProp: files => {
            props.onUpdate(files);
        },
    });
    const uploadFilesWrapped = (fileList) => {
        if (props.onUpload) {
            rs.updateState({ isUploadingProgress: 0 });
            props
                .onUpload(fileList)
                .then(uploadedFiles => {
                rs.updateState({
                    files: uploadedFiles,
                    isUploadingProgress: null,
                });
                rs.props.onUpdate(uploadedFiles);
            })
                .catch(console.error);
            return;
        }
        uploadFiles({
            fileList: fileList,
            onStart: () => {
                rs.updateState({ isUploadingProgress: 0 });
            },
            onUpdateProgress: perc => {
                rs.updateState({ isUploadingProgress: perc });
            },
            onComplete: uploadedFiles => {
                rs.updateState({
                    files: uploadedFiles,
                    isUploadingProgress: null,
                });
                rs.props.onUpdate(uploadedFiles);
            },
        });
    };
    return (React.createElement("div", { className: BuildClass({
            [rs.props.className ?? '']: true,
            'relative h-full font-light text-[#888]': true,
            'border-2 border-dashed rounded-md': true,
            'cursor-pointer': true,
            'bg-[#ffe] hover:bg-[#ffb]': !rs.state.isDraggingOver && !rs.state.isFocused,
            'bg-[#ffb]': rs.state.isFocused && !rs.state.isDraggingOver,
            'bg-[#ff9]': rs.state.isDraggingOver,
            'border-[#ccc] hover:border-[#aaa]': !rs.state.isDraggingOver && !rs.state.isFocused,
            'border-[#00f]': rs.state.isFocused && !rs.state.isDraggingOver,
            'border-[#000]': rs.state.isDraggingOver,
        }), style: { height: rs.props.height }, onDragEnter: ev => {
            ev.preventDefault();
            rs.updateState({ isDraggingOver: true });
        }, onDragOver: ev => {
            ev.preventDefault();
            rs.updateState({ isDraggingOver: true });
        }, onDragLeave: ev => {
            ev.preventDefault();
            rs.updateState({ isDraggingOver: false });
        }, onDrop: ev => {
            ev.preventDefault();
            rs.updateState({ isDraggingOver: false });
            uploadFilesWrapped(ev.dataTransfer.files);
        } },
        React.createElement("input", { className: "pointer-events-none w-0 h-0 overflow-hidden", type: "file", ref: rs.refs.inputRef, multiple: rs.props.multiple, onFocus: () => {
                rs.updateState({ isFocused: true });
            }, onBlur: () => {
                rs.updateState({ isFocused: false });
            }, onChange: async (ev) => {
                uploadFilesWrapped(ev.target.files);
                ev.target.value = '';
            } }),
        React.createElement("span", { className: "absolute inline-block top-1/2 w-full translate-y-[-50%] text-center" },
            React.createElement(MatchRender, { check: [
                    {
                        when: rs.state.isUploadingProgress !== null,
                        then: React.createElement(React.Fragment, null,
                            "Uploading ",
                            rs.state.isUploadingProgress,
                            "%"),
                    },
                    {
                        when: rs.state.isDraggingOver,
                        then: React.createElement(React.Fragment, null, "Drop to upload"),
                    },
                    {
                        when: rs.state.files.length > 0,
                        then: (React.createElement(React.Fragment, null, rs.state.files.map(file => (React.createElement(AttachedFileLine, { key: file.Checksum, file: file, onRemove: () => {
                                const files = rs.state.files.filter(f => f.Checksum !== file.Checksum);
                                rs.updateState({ files: files });
                                rs.props.onUpdate(files);
                            } }))))),
                    },
                    {
                        when: true,
                        then: (React.createElement(React.Fragment, null, rs.props.placeholder ?? 'Click or drag files here')),
                    },
                ] }))));
};
const AttachedFileLine = (props) => {
    const [isDownloadHovered, setIsDownloadHovered] = React.useState(false);
    const [isDeleteHovered, setIsDeleteHovered] = React.useState(false);
    return (React.createElement("div", { className: BuildClass({
            'flex items-center m-1 rounded-xs': true,
            'bg-red-200': isDeleteHovered,
            'bg-blue-200': isDownloadHovered,
            'hover:bg-[#9df]': !isDeleteHovered && !isDownloadHovered,
        }) },
        React.createElement("span", { className: BuildClass({
                [TW_OVERFLOW_ELLIPSIS]: true,
                'flex-grow align-baseline': true,
                'leading-6 font-normal': true,
                'text-neutral-800': true,
            }) }, props.file.Filename),
        React.createElement("span", { className: BuildClass({
                'shrink-0 align-baseline': true,
                'w-6 h-6 p-1 ml-1': true,
                'border border-transparent rounded-xs': true,
                'cursor-pointer hover:border-[#00F] hover:bg-blue-300': true,
            }), onMouseEnter: () => {
                setIsDownloadHovered(true);
            }, onMouseLeave: () => {
                setIsDownloadHovered(false);
            }, onClick: e => {
                e.preventDefault();
                e.stopPropagation();
                fetch(`https://storage.trionline.com.au/get/${props.file.Checksum}/`)
                    .then(async (x) => x.blob())
                    .then(file => {
                    const url = window.URL.createObjectURL(file);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = props.file.Filename || 'download';
                    document.body.appendChild(a);
                    a.click();
                    setTimeout(() => {
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(url);
                    }, 0);
                });
            } },
            React.createElement("img", { className: "w-4 h-4", src: "/static/img/i8/color-copy.svg", alt: "Download attachment" })),
        React.createElement("span", { className: BuildClass({
                'shrink-0 align-baseline': true,
                'w-6 h-6 p-1 ml-1': true,
                'border border-transparent rounded-xs': true,
                'cursor-pointer hover:border-[#f00] hover:bg-red-300': true,
            }), onMouseEnter: () => {
                setIsDeleteHovered(true);
            }, onMouseLeave: () => {
                setIsDeleteHovered(false);
            }, onClick: e => {
                e.preventDefault();
                e.stopPropagation();
                props.onRemove();
            } },
            React.createElement("img", { className: "w-4 h-4", src: "/static/img/i8/w11-minus.svg", alt: "Remove attachment" }))));
};
const uploadFiles = async (input) => {
    input.onStart();
    const files = Array.from(input.fileList ?? []);
    const progress = _.times(files.length, () => 0);
    const uploadedFiles = await Promise.all(files.map(async (file, index) => new Promise(async (resolve) => {
        const uploader = uploadFileIris({
            fileObj: file,
            onProgress: e => {
                progress[index] = e.percentage;
                const perc = Math.floor(_.sum(progress) / files.length);
                input.onUpdateProgress(perc);
            },
            onComplete: async (e) => {
                resolve({
                    Filename: e.file.name,
                    Checksum: e.checksum,
                    Data: Buffer.from(await e.file.arrayBuffer()).toString('base64'),
                });
            },
        });
        uploader();
    })));
    input.onComplete(uploadedFiles);
    return uploadedFiles;
};
const getDefaultStateDropZone = () => ({
    isUploadingProgress: null,
    isDraggingOver: false,
    isFocused: false,
    isProcessing: false,
});
export const FileUploadDropZone = (props) => {
    const rs = useRSInstance({
        props,
        defaultState: getDefaultStateDropZone,
        refs: {
            inputRef: React.useRef(stubInput),
        },
        actionToDelta: null,
    });
    const uploadFilesWrapped = (fileList) => {
        uploadFiles({
            fileList: fileList,
            onStart: () => {
                rs.updateState({ isUploadingProgress: 0 });
            },
            onUpdateProgress: perc => {
                rs.updateState({ isUploadingProgress: perc });
            },
            onComplete: uploadedFiles => {
                rs.updateState({
                    isUploadingProgress: null,
                    isProcessing: true,
                });
                rs.props
                    .onUpload(uploadedFiles)
                    .then(() => {
                    rs.updateState({ isProcessing: false });
                })
                    .catch(console.error);
            },
        });
    };
    return (React.createElement("div", { className: BuildClass({
            'relative h-full': true,
            [props.className ?? '']: true,
        }), onDragEnter: ev => {
            ev.preventDefault();
            if (!props.disabled) {
                rs.updateState({ isDraggingOver: true });
            }
        }, onDragOver: ev => {
            ev.preventDefault();
            if (!props.disabled) {
                rs.updateState({ isDraggingOver: true });
            }
        }, onDragLeave: ev => {
            ev.preventDefault();
            if (!props.disabled) {
                rs.updateState({ isDraggingOver: false });
            }
        }, onDrop: ev => {
            ev.preventDefault();
            if (!props.disabled) {
                rs.updateState({ isDraggingOver: false });
                uploadFilesWrapped(ev.dataTransfer.files);
            }
        } },
        React.createElement("div", { className: BuildClass({
                'absolute z-10 top-0 left-0 h-full w-full': true,
                'pointer-events-none opacity-0': !Boolean(rs.state.isDraggingOver ||
                    rs.state.isUploadingProgress !== null ||
                    rs.state.isProcessing),
            }) },
            React.createElement("input", { className: "pointer-events-none w-0 h-0 overflow-hidden", type: "file", tabIndex: -1, ref: rs.refs.inputRef, multiple: rs.props.multiple, onChange: async (ev) => {
                    if (!props.disabled) {
                        uploadFilesWrapped(ev.target.files);
                        ev.target.value = '';
                    }
                } }),
            React.createElement("div", { className: BuildClass({
                    'absolute z-10 top-0 left-0 m-[2px]': true,
                    'h-[calc(100%-4px)] w-[calc(100%-4px)]': true,
                    'text-center grid place-items-center': true,
                    'border-2 border-dashed rounded-md': true,
                    'border-[#000]': true,
                    'bg-[hsla(60,100%,80%,0.5)]': true,
                    'text-neutral-600 text-2xl': true,
                }) },
                React.createElement(CJSX, { cond: rs.state.isDraggingOver },
                    React.createElement(React.Fragment, null, props.draggingOverMessage ?? 'Drop to upload files')),
                React.createElement(CJSX, { cond: !rs.state.isDraggingOver },
                    React.createElement("div", { className: "text-center" },
                        React.createElement(LoadingSpinnerLarge, null),
                        React.createElement("div", { className: "text-neutral-600 text-2xl" },
                            React.createElement(CJSX, { cond: rs.state.isProcessing },
                                React.createElement(React.Fragment, null, "Processing...")),
                            React.createElement(CJSX, { cond: !rs.state.isProcessing },
                                React.createElement(React.Fragment, null,
                                    rs.state.isUploadingProgress,
                                    "%"))))))),
        props.children));
};
export const FileUploadTemp = (props) => (React.createElement(FileUpload, { ..._.omit(props, 'multiple', 'onUpload'), multiple: false, onUpload: async (fileList) => Promise.all(Array.from(fileList ?? []).map(async (x) => ({
        Filename: x.name,
        Checksum: null,
        Data: Buffer.from(await x.arrayBuffer()).toString('base64'),
    }))) }));
//# sourceMappingURL=file-upload.js.map